import { BusinessObjectBase } from './base';
import { pdfDocumentSchema } from '@schemas/business-objects/pdf-document';
import type { Resource } from './resource';
import type { TestArticle } from './test-article';
import type { Id } from '../base-types';
import type { TextBox } from './text-box';
import type { User } from './user';

export interface PdfDocumentData {
  readonly id: Id;
  readonly fileName?: string;
  readonly mimeType?: string;
  readonly size?: number;
  readonly base64Content?: string;
  readonly resourceId: Id;
  readonly resource?: Resource;
  readonly testArticleId: Id;
  readonly testArticle?: TestArticle;
  readonly locked?: boolean;
}

export interface PdfDocument extends Readonly<PdfDocumentData> {}
type PdfDocumentCreateWithChildrenData = Partial<PdfDocumentData>;

export class PdfDocument extends BusinessObjectBase {
  static readonly entityName = 'PdfDocument' as const;
  static readonly plural = 'pdf-documents';
  static schema = pdfDocumentSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<PdfDocument[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new PdfDocument(data));
  }

  static async findOne(filter?: any): Promise<PdfDocument | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new PdfDocument(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<PdfDocument> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new PdfDocument(data);
  }

  static async create(data: Omit<PdfDocumentData, 'id'>): Promise<PdfDocument> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new PdfDocument(result);
  }

  static async createMany(
    data: Omit<PdfDocumentData, 'id'>[],
  ): Promise<PdfDocument[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new PdfDocument(item));
  }

  static async createWithChildren(
    data: PdfDocumentCreateWithChildrenData,
  ): Promise<PdfDocument> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new PdfDocument(result);
  }

  static async createManyWithChildren(
    data: PdfDocumentCreateWithChildrenData[],
  ): Promise<PdfDocument[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new PdfDocument(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<PdfDocumentData>,
  ): Promise<PdfDocument> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new PdfDocument(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async dataForCopy(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${PdfDocument.plural}/${(this as any).id}/data-for-copy`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async content(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${PdfDocument.plural}/${(this as any).id}/content`;
    return BusinessObjectBase.get<any>(url);
  }
}
